import { useState, useEffect, useCallback } from 'react';
import api from '../api/offlineClient';
import { fmtFecha, EstadoBadge, STITLE, ROW, BTN_OK, BTN_DEL } from './Calendario';

// Acceso directo de admin/jefe/administrativo a lo relacionado con vacaciones
// (aprobar/rechazar, resumen del año, días pendientes de años anteriores).
// Calendario.js sigue teniendo lo mismo integrado — esta página no le quita
// nada, es solo un atajo más centrado.
export default function Vacaciones() {
  const today = new Date();

  const [año,      setAño]      = useState(today.getFullYear());
  const [pendAño,  setPendAño]  = useState(today.getFullYear() - 1);

  const [allVacaciones, setAllVacaciones] = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [vacPend,       setVacPend]       = useState({});
  const [savingVacPend, setSavingVacPend] = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [pendOpen,      setPendOpen]      = useState(false);
  const [editingEmp,    setEditingEmp]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, emps] = await Promise.all([
        api.get('/api/calendar/vacaciones/all').catch(() => ({ data: [] })),
        api.get('/api/employees/list').catch(() => ({ data: [] })),
      ]);
      setAllVacaciones(all.data || []);
      setEmployees((emps.data || []).filter(e => e.user_id));
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  const loadVacPend = useCallback(async () => {
    try {
      const pend = await api.get(`/api/calendar/vacaciones-pendientes?año=${pendAño}`).catch(() => ({ data: [] }));
      const pendMap = {};
      (pend.data || []).forEach(p => { pendMap[p.employee_id] = p.dias; });
      setVacPend(pendMap);
    } catch { /* noop */ }
  }, [pendAño]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadVacPend(); }, [loadVacPend]);

  async function responderVac(id, estado) {
    try { await api.put(`/api/calendar/vacaciones/${id}`, { estado }); await load(); window.dispatchEvent(new CustomEvent('sgv:pendientes')); }
    catch (e) { alert(e.message); }
  }

  async function saveVacPend(employeeId, dias) {
    setSavingVacPend(employeeId);
    try {
      await api.put('/api/calendar/vacaciones-pendientes', { employee_id: employeeId, año: pendAño, dias });
      setVacPend(p => ({ ...p, [employeeId]: dias }));
      setEditingEmp(null);
    } catch (e) { alert(e.message); }
    finally { setSavingVacPend(null); }
  }

  const pendientes = allVacaciones.filter(v => v.estado === 'pendiente');
  const delAño = allVacaciones.filter(v => new Date(v.fecha_inicio).getFullYear() === año);

  if (loading) return <div style={{ padding: 32, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>;

  return (
    <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 16 }}>Vacaciones</div>

      {/* ── Solicitudes pendientes ─────────────────────────────────────────── */}
      <div style={{ background: 'var(--surface)', border: '1.5px solid rgba(96,165,250,.3)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
        <div style={{ ...STITLE, color: '#60a5fa' }}>⏳ Solicitudes pendientes ({pendientes.length})</div>
        {pendientes.length === 0
          ? <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Sin solicitudes pendientes.</div>
          : <div style={{ display: 'grid', gap: 6 }}>
              {pendientes.map(v => (
                <div key={v.id} style={ROW}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{v.user_nombre}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>{fmtFecha(v.fecha_inicio)} → {fmtFecha(v.fecha_fin)} · {v.dias} día{v.dias !== 1 ? 's' : ''}</div>
                    {v.nota && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{v.nota}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 7 }}>
                    <button onClick={() => responderVac(v.id, 'aprobada')}  style={BTN_OK}>✓ Aprobar</button>
                    <button onClick={() => responderVac(v.id, 'rechazada')} style={BTN_DEL}>✗ Rechazar</button>
                  </div>
                </div>
              ))}
            </div>
        }
      </div>

      {/* ── Resumen del año ───────────────────────────────────────────────── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
          <div style={{ ...STITLE, marginBottom: 0 }}>Resumen vacaciones</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => setAño(a => a - 1)} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-dim)', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>‹</button>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--amber)', minWidth: 36, textAlign: 'center' }}>{año}</span>
            <button onClick={() => setAño(a => a + 1)} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-dim)', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>›</button>
          </div>
        </div>
        {delAño.length === 0
          ? <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Sin solicitudes este año.</div>
          : <div style={{ display: 'grid', gap: 5 }}>
              {delAño.map(v => (
                <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', minWidth: 130 }}>{v.user_nombre}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{fmtFecha(v.fecha_inicio)} → {fmtFecha(v.fecha_fin)} · {v.dias}d</span>
                  <EstadoBadge estado={v.estado} tipo={v.tipo} />
                </div>
              ))}
            </div>
        }
      </div>

      {/* ── Días pendientes de años anteriores (arrastre manual) ───────────── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: pendOpen ? '14px 16px' : '10px 16px', marginBottom: 12 }}>
        <div
          onClick={() => setPendOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--text-dim)', transition: 'transform .15s', transform: pendOpen ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▶</span>
            <div style={{ ...STITLE, marginBottom: 0 }}>Vacaciones pendientes por persona</div>
          </div>
          {pendOpen && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={ev => ev.stopPropagation()}>
              <button onClick={() => setPendAño(a => a - 1)} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-dim)', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>‹</button>
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--amber)', minWidth: 36, textAlign: 'center' }}>{pendAño}</span>
              <button onClick={() => setPendAño(a => a + 1)} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-dim)', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>›</button>
            </div>
          )}
        </div>
        {pendOpen && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', margin: '10px 0' }}>
              Días de {pendAño} que cada persona tiene todavía pendientes de disfrutar (arrastre manual —
              no se calcula solo, hay que rellenarlo).
            </div>
            {employees.length === 0
              ? <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Sin empleados.</div>
              : <div style={{ display: 'grid', gap: 4 }}>
                  {employees.map(e => {
                    const editing = editingEmp === e.id;
                    return (
                      <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', flex: 1 }}>{e.nombre}</span>
                        {editing ? (
                          <>
                            <input
                              type="number" min="0" step="0.5" autoFocus
                              value={vacPend[e.id] ?? ''}
                              placeholder="0"
                              onChange={ev => setVacPend(p => ({ ...p, [e.id]: ev.target.value }))}
                              onKeyDown={ev => { if (ev.key === 'Enter') saveVacPend(e.id, ev.target.value === '' ? 0 : parseFloat(ev.target.value)); if (ev.key === 'Escape') setEditingEmp(null); }}
                              onBlur={ev => saveVacPend(e.id, ev.target.value === '' ? 0 : parseFloat(ev.target.value))}
                              style={{ background: 'var(--surface-deep)', border: '1.5px solid var(--amber)', borderRadius: 6, color: 'var(--text)', padding: '5px 8px', width: 70, textAlign: 'right', fontSize: 13, fontFamily: 'inherit' }}
                            />
                            <span style={{ fontSize: 11, color: 'var(--text-dim)', width: 66 }}>
                              {savingVacPend === e.id ? 'guardando…' : 'días'}
                            </span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', width: 70, textAlign: 'right' }}>{vacPend[e.id] ?? 0}</span>
                            <button
                              onClick={() => setEditingEmp(e.id)}
                              title="Editar"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-dim)', padding: '2px 6px', width: 66, textAlign: 'left' }}
                            >
                              ✏️ días
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
            }
          </>
        )}
      </div>
    </div>
  );
}
